/**
 * Controls — the DOM chrome: top bar (logo, language, how-to, balance) and
 * the bottom control bar (bet, difficulty, Play/GO/CASH OUT), plus toasts and
 * the how-to-play modal. Localized (Arabic default, English toggle).
 */
import { formatMoney } from '../game/Economy.js';
import { formatMultiplier } from '../game/Difficulty.js';
import { DIFFICULTIES } from '../game/Difficulty.js';
import { t, getLang, setLang } from './i18n.js';
import { CONFIG, AFFILIATE_LINK } from '../config.js';

const CHIPS = [0.5, 1, 2, 7, 25, 100];

export default class Controls {
  constructor({ bus, game, economy, coinIcon }) {
    this.bus = bus;
    this.game = game;
    this.economy = economy;
    this.t = t();

    document.documentElement.lang = getLang();
    document.documentElement.dir = this.t.dir;

    this.top = document.getElementById('topbar');
    this.bar = document.getElementById('controls');
    this.overlay = document.getElementById('overlay');
    this._buildTop(coinIcon);
    this._buildBar();
    this._buildModal();
    this._wire();
    this._render();
  }

  /* ---------------- DOM scaffolding ---------------- */

  _buildTop(coinIcon) {
    const T = this.t;
    this.coinIcon = coinIcon;
    this.top.innerHTML = `
      <div class="logo">CHICKEN<span>ROAD</span></div>
      <div class="top-right">
        <a class="real-btn" href="${AFFILIATE_LINK}" target="_blank" rel="noopener">${T.realMoneyShort}</a>
        <button class="lang-btn" id="lang-btn">${T.toggleLabel}</button>
        <button class="howto" id="howto-btn">${T.howto}</button>
        <div class="balance">
          <img class="coin-ico" alt="" src="${coinIcon}" />
          <span id="balance-amt">0.00</span><span class="cur">$</span>
        </div>
      </div>`;
  }

  _buildBar() {
    const T = this.t;
    this.bar.innerHTML = `
      <div class="bet-block">
        <div class="block-label">${T.bet}</div>
        <div class="bet-row">
          <button class="mini" id="bet-min">MIN</button>
          <input id="bet-input" type="text" inputmode="decimal" autocomplete="off" />
          <button class="mini" id="bet-max">MAX</button>
        </div>
        <div class="chips">
          ${CHIPS.map((v) => `<button class="chip" data-v="${v}">${v}</button>`).join('')}
        </div>
      </div>
      <div class="diff-block">
        <div class="block-label">${T.difficulty}</div>
        <div class="segment" id="diff-seg">
          ${DIFFICULTIES.map((d) => `<button data-id="${d.id}" class="${d.id === 'easy' ? 'on' : ''}">${T.diffNames[d.id]}</button>`).join('')}
        </div>
        <div class="odds" id="odds-line"></div>
      </div>
      <div class="action-block">
        <button id="cashout-btn" class="cashout" hidden>
          <span class="co-label">${T.cashout}</span>
          <span class="co-amt" id="cashout-amt">$0.00</span>
        </button>
        <button id="play-btn" class="play">${T.play}</button>
      </div>`;

    this.el = {
      balance: document.getElementById('balance-amt'),
      betInput: document.getElementById('bet-input'),
      diffSeg: document.getElementById('diff-seg'),
      odds: document.getElementById('odds-line'),
      play: document.getElementById('play-btn'),
      cashout: document.getElementById('cashout-btn'),
      cashoutAmt: document.getElementById('cashout-amt'),
    };
  }

  _buildModal() {
    const T = this.t;
    this.modal = document.createElement('div');
    this.modal.className = 'modal-back';
    this.modal.hidden = true;
    this.modal.innerHTML = `
      <div class="modal">
        <h2>${T.modalTitle}</h2>
        <ol>${T.modalSteps.map((s) => `<li>${s}</li>`).join('')}</ol>
        <p class="fair" id="fair-line"></p>
        <button class="modal-close" id="modal-close">${T.gotIt}</button>
      </div>`;
    this.overlay.appendChild(this.modal);

    // end-of-round bonus offer dialog (shows the partner promo on win or loss)
    this.claim = document.createElement('div');
    this.claim.className = 'modal-back';
    this.claim.hidden = true;
    this.claim.innerHTML = `
      <div class="modal claim-modal">
        <p class="bonus-outcome" id="bonus-outcome"></p>
        <div class="bonus-offer">
          <span class="bonus-gift" aria-hidden="true">🎁</span>
          <h2 class="bonus-offer-title" id="bonus-offer-title"></h2>
          <p class="bonus-offer-sub" id="bonus-offer-sub"></p>
        </div>
        <a class="claim-yes" id="claim-yes" href="${AFFILIATE_LINK}" target="_blank" rel="noopener">${T.bonusCta}</a>
        <button class="claim-no" id="claim-no">${T.bonusDismiss}</button>
      </div>`;
    this.overlay.appendChild(this.claim);

    this.toastHost = document.createElement('div');
    this.toastHost.className = 'toast-host';
    this.overlay.appendChild(this.toastHost);
  }

  /* ---------------- events ---------------- */

  _wire() {
    const { el } = this;
    const click = () => this.bus.emit('sfx:click', {});

    document.querySelector('.real-btn')?.addEventListener('click', () => {
      window.fbq?.('track', 'Lead', { content_name: 'real_money_cta', content_category: 'game_topbar' });
    });

    document.getElementById('lang-btn').addEventListener('click', () => {
      setLang(getLang() === 'ar' ? 'en' : 'ar');
      location.reload();
    });

    el.play.addEventListener('click', () => {
      click();
      if (this.game.state === 'idle') this.game.start();
      else if (this.game.state === 'waiting') this.game.go();
    });
    el.cashout.addEventListener('click', () => {
      click();
      this.game.cashOut();
    });

    // space = Play/GO, C = cash out (with debounce handled by the state machine)
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (this.game.state === 'idle') this.game.start();
        else if (this.game.state === 'waiting') this.game.go();
      } else if (e.key.toLowerCase() === 'c') {
        this.game.cashOut();
      }
    });

    el.betInput.addEventListener('change', () => {
      const v = parseFloat(el.betInput.value.replace(',', '.'));
      this.economy.setBet(Number.isFinite(v) ? v : this.economy.bet);
      this._render();
    });
    document.getElementById('bet-min').addEventListener('click', () => {
      click();
      this.economy.setBet(this.economy.minBet);
      this._render();
    });
    document.getElementById('bet-max').addEventListener('click', () => {
      click();
      this.economy.setBet(this.economy.maxBet);
      this._render();
    });
    this.bar.querySelectorAll('.chip').forEach((chip) =>
      chip.addEventListener('click', () => {
        click();
        this.economy.setBet(parseFloat(chip.dataset.v));
        this._render();
      })
    );

    el.diffSeg.querySelectorAll('button').forEach((btn) =>
      btn.addEventListener('click', () => {
        click();
        this.game.setDifficulty(btn.dataset.id);
      })
    );

    document.getElementById('howto-btn').addEventListener('click', async () => {
      click();
      const commit = await this.game.rng.commitment();
      document.getElementById('fair-line').textContent = this.t.fair(commit.slice(0, 20));
      this.modal.hidden = false;
    });
    document.getElementById('modal-close').addEventListener('click', () => (this.modal.hidden = true));
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.modal.hidden = true;
    });

    this.bus.on('game:state', () => this._render());
    this.bus.on('game:difficulty', () => this._render());
    this.bus.on('economy:balance', () => this._render());
    this.bus.on('economy:bet', () => this._render());
    this.bus.on('chicken:landed', ({ potential }) => {
      this.el.cashoutAmt.textContent = `$${formatMoney(potential)}`;
      this._flash(this.el.cashout);
    });
    this.bus.on('economy:rejected', () => this._shake(this.el.betInput.parentElement));
    this.bus.on('economy:topup', () => this._toast('lose', this.t.topup));

    this.bus.on('round:cashout', ({ multiplier, payout }) => {
      this._toast('win', '', (node) => {
        node.innerHTML = `<b class="toast-mult">${formatMultiplier(multiplier)}x</b><span class="toast-amt">+$0.00</span>`;
        const amt = node.querySelector('.toast-amt');
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - t0) / 750);
          amt.textContent = `+$${formatMoney(payout * (1 - Math.pow(1 - p, 3)))}`;
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    });
    this.bus.on('round:dead', ({ lost }) => {
      this._toast('lose', this.t.lost(formatMoney(lost)));
    });

    // after every round — win (cashout) OR loss — surface the partner bonus offer
    this.bus.on('round:cashout', () => this._showBonus('win'));
    this.bus.on('round:dead', () => this._showBonus('lose'));
    document.getElementById('claim-yes').addEventListener('click', () => {
      this.bus.emit('sfx:click', {});
      window.fbq?.('track', 'Lead', { content_name: 'real_money_cta', content_category: 'game_claim_modal' });
      this.claim.hidden = true;
    });
    document.getElementById('claim-no').addEventListener('click', () => {
      this.bus.emit('sfx:click', {});
      this.claim.hidden = true;
    });
    this.claim.addEventListener('click', (e) => {
      if (e.target === this.claim) this.claim.hidden = true;
    });
  }

  /* show the partner-bonus offer after a round ends (win or loss) */
  _showBonus(outcome) {
    setTimeout(() => {
      // don't interrupt if the player already started a new round or a modal is open
      const busy =
        this.game.state === 'hopping' || this.game.state === 'waiting' || this.game.state === 'starting';
      if (busy || !this.modal.hidden || !this.claim.hidden) return;

      const lang = getLang();
      const bonus = CONFIG.bonus || {};
      document.getElementById('bonus-outcome').textContent =
        outcome === 'win' ? this.t.bonusWin : this.t.bonusLose;
      document.getElementById('bonus-offer-title').innerHTML = bonus.title?.[lang] || '';
      document.getElementById('bonus-offer-sub').innerHTML = bonus.sub?.[lang] || '';
      this.claim.querySelector('.claim-modal').dataset.outcome = outcome;
      this.claim.hidden = false;
      if (outcome === 'win') this._claimBurst();
    }, outcome === 'win' ? 1400 : 1100);
  }

  /* a short confetti-and-coins shower inside the bonus dialog */
  _claimBurst() {
    const host = this.claim.querySelector('.claim-modal');
    host.querySelector('.claim-burst')?.remove();
    const burst = document.createElement('div');
    burst.className = 'claim-burst';
    const COLORS = ['#3ecf52', '#ff6b2e', '#ffd24a', '#4a90e2', '#ef5350', '#9b6dd6'];
    let html = '';
    for (let i = 0; i < 22; i++) {
      const w = 6 + Math.random() * 6;
      html += `<i style="left:${Math.random() * 100}%;width:${w}px;height:${w * 0.45}px;background:${COLORS[i % COLORS.length]};animation-delay:${Math.random() * 0.5}s;animation-duration:${1.3 + Math.random() * 0.9}s"></i>`;
    }
    if (this.coinIcon) {
      for (let i = 0; i < 4; i++) {
        html += `<img src="${this.coinIcon}" alt="" style="left:${8 + Math.random() * 84}%;animation-delay:${Math.random() * 0.6}s;animation-duration:${1.5 + Math.random() * 0.7}s" />`;
      }
    }
    burst.innerHTML = html;
    host.appendChild(burst);
    setTimeout(() => burst.remove(), 3200);
  }

  /* ---------------- rendering ---------------- */

  _render() {
    const { el } = this;
    const T = this.t;
    const g = this.game;
    const idle = g.state === 'idle';
    const inRound = g.state === 'waiting' || g.state === 'hopping';

    el.balance.textContent = formatMoney(this.economy.balance);
    if (document.activeElement !== el.betInput) {
      el.betInput.value = formatMoney(this.economy.bet);
    }

    // bet + difficulty lock during a round
    this.bar.querySelectorAll('.bet-block button, .bet-block input, .diff-block button')
      .forEach((n) => (n.disabled = !idle));
    this.bar.classList.toggle('locked', !idle);

    el.diffSeg.querySelectorAll('button').forEach((b) =>
      b.classList.toggle('on', b.dataset.id === g.difficulty.id));
    el.odds.innerHTML = T.odds(g.difficulty.K);

    el.play.textContent = inRound ? T.go : T.play;
    el.play.disabled = g.state === 'starting' || g.state === 'hopping' || g.state === 'dead' || g.state === 'cashed';
    el.play.classList.toggle('go', inRound);

    const showCash = inRound && g.lane >= 1;
    el.cashout.hidden = !showCash;
    el.cashout.disabled = g.state !== 'waiting';
    if (showCash) el.cashoutAmt.textContent = `$${formatMoney(g.potential)}`;
  }

  _toast(kind, text, build) {
    const node = document.createElement('div');
    node.className = `toast ${kind}`;
    if (build) build(node);
    else node.textContent = text;
    this.toastHost.appendChild(node);
    requestAnimationFrame(() => node.classList.add('show'));
    setTimeout(() => {
      node.classList.remove('show');
      setTimeout(() => node.remove(), 350);
    }, 2100);
  }

  _shake(node) {
    node.classList.remove('shake');
    void node.offsetWidth;
    node.classList.add('shake');
  }

  _flash(node) {
    node.classList.remove('flash');
    void node.offsetWidth;
    node.classList.add('flash');
  }
}
